import React, { useMemo, useState } from 'react'
import axios from 'axios'

import { getBackendUrl } from 'helpers/env'

function CheckExternalMenu() {
	const [credentials, setCredentials] = useState({
		client_id: '',
		client_secret: '',
		pos_id: ''
	})
	const [loading, setLoading] = useState(false)
	const [error, setError] = useState('')
	const [token, setToken] = useState('')
	const [compositionData, setCompositionData] = useState(null)
	const [viewMode, setViewMode] = useState('table')

	const apiBaseUrl = getBackendUrl()

	const tableColumns = useMemo(() => {
		if (!Array.isArray(compositionData) || compositionData.length === 0) return []
		const keySet = new Set()
		compositionData.forEach((item) => {
			if (item && typeof item === 'object' && !Array.isArray(item)) {
				Object.keys(item).forEach((key) => keySet.add(key))
			}
		})
		return Array.from(keySet)
	}, [compositionData])

	const isArrayOfObjects = useMemo(() => {
		return (
			Array.isArray(compositionData) &&
			compositionData.length > 0 &&
			compositionData.every((item) => item && typeof item === 'object' && !Array.isArray(item))
		)
	}, [compositionData])

	const handleChange = (event) => {
		const { name, value } = event.target
		setCredentials((prevState) => ({ ...prevState, [name]: value }))
	}

	const getValueForTable = (value) => {
		if (value === null || value === undefined) return '-'
		if (typeof value === 'object') return JSON.stringify(value)
		return String(value)
	}

	const handleSubmit = async (event) => {
		event.preventDefault()
		setError('')
		setToken('')
		setCompositionData(null)

		if (!credentials.client_id || !credentials.client_secret || !credentials.pos_id) {
			setError('Заполните client_id, client_secret и pos_id.')
			return
		}

		try {
			setLoading(true)

			const loginResponse = await axios.post(
				`${apiBaseUrl}/integrations/uzum/security/oauth/token`,
				{
					client_id: credentials.client_id,
					client_secret: credentials.client_secret,
				}
			)

			const accessToken =
				loginResponse?.data?.access_token ||
				loginResponse?.data?.data?.access_token ||
				loginResponse?.data?.token ||
				loginResponse?.data?.data?.token

			if (!accessToken) {
				throw new Error('Токен не найден в ответе /security/oauth/token')
			}

			setToken(accessToken)

			const compositionResponse = await axios.get(
				`${apiBaseUrl}/integrations/uzum/menu/${credentials.pos_id}/composition`,
				{
					headers: {
						Authorization: `Bearer ${accessToken}`
					}
				}
			)

			setCompositionData(compositionResponse?.data ?? null)
		} catch (requestError) {
			const message =
				requestError?.response?.data?.message ||
				requestError?.message ||
				'Ошибка при получении composition'
			setError(message)
		} finally {
			setLoading(false)
		}
	}

	return (
		<div className="container py-4">
			<h4 className="mb-3">Uzum Composition Viewer</h4>

			<form onSubmit={handleSubmit} className="mb-3">
				<div className="row g-2 mb-2">
					<div className="col-md-4">
						<label className="form-label">client_id</label>
						<input
							type="text"
							className="form-control"
							name="client_id"
							value={credentials.client_id}
							onChange={handleChange}
							placeholder="Введите client_id"
						/>
					</div>
					<div className="col-md-4">
						<label className="form-label">client_secret</label>
						<input
							type="text"
							className="form-control"
							name="client_secret"
							value={credentials.client_secret}
							onChange={handleChange}
							placeholder="Введите client_secret"
						/>
					</div>
					<div className="col-md-4">
						<label className="form-label">pos_id</label>
						<input
							type="text"
							className="form-control"
							name="pos_id"
							value={credentials.pos_id}
							onChange={handleChange}
							placeholder="Введите pos_id"
						/>
					</div>
				</div>
				<button type="submit" className="btn btn-primary" disabled={loading}>
					{loading ? 'Загрузка...' : 'Авторизоваться и получить composition'}
				</button>
			</form>

			{token && (
				<div className="alert alert-success py-2">
					Токен получен успешно. Composition загружен.
				</div>
			)}

			{error && <div className="alert alert-danger py-2">{error}</div>}

			{compositionData !== null && (
				<>
					<div className="d-flex gap-2 mb-3">
						<button
							type="button"
							className={`btn ${viewMode === 'table' ? 'btn-dark' : 'btn-outline-dark'}`}
							onClick={() => setViewMode('table')}
						>
							Таблица
						</button>
						<button
							type="button"
							className={`btn ${viewMode === 'json' ? 'btn-dark' : 'btn-outline-dark'}`}
							onClick={() => setViewMode('json')}
						>
							JSON
						</button>
					</div>

					{viewMode === 'json' && (
						<pre className="bg-light border rounded p-3" style={{ maxHeight: 600, overflow: 'auto' }}>
							{JSON.stringify(compositionData, null, 2)}
						</pre>
					)}

					{viewMode === 'table' && (
						<>
							{isArrayOfObjects ? (
								<div className="table-responsive">
									<table className="table table-striped table-bordered">
										<thead>
											<tr>
												{tableColumns.map((column) => (
													<th key={column}>{column}</th>
												))}
											</tr>
										</thead>
										<tbody>
											{compositionData.map((row, rowIndex) => (
												<tr key={`row-${rowIndex}`}>
													{tableColumns.map((column) => (
														<td key={`${rowIndex}-${column}`}>
															{getValueForTable(row[column])}
														</td>
													))}
												</tr>
											))}
										</tbody>
									</table>
								</div>
							) : (
								<div className="table-responsive">
									<table className="table table-bordered">
										<thead>
											<tr>
												<th style={{ width: 280 }}>Поле</th>
												<th>Значение</th>
											</tr>
										</thead>
										<tbody>
											{Object.entries(compositionData || {}).map(([key, value]) => (
												<tr key={key}>
													<td>{key}</td>
													<td>{getValueForTable(value)}</td>
												</tr>
											))}
										</tbody>
									</table>
								</div>
							)}
						</>
					)}
				</>
			)}
		</div>
	)
}

export default CheckExternalMenu